import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import useOperationalHealth from '../../hooks/useOperationalHealth';
import { startAllMonitors, createMissingMonitors } from '../../services/MonitorsService';
import { activateByActionType, activateSupportingAutomations } from '../../services/AutomationsService';
import { ensureSystemTicker } from '../../services/BeholderService';
import { provisionAllAi } from '../../services/StrategiesService';
import { confirmDialog } from '../../utils/ConfirmDialog';

const SEVERITY_STYLE = {
    red:    { badge: 'bg-danger',  icon: '🔴' },
    yellow: { badge: 'bg-warning text-dark', icon: '🟡' }
};

function OperationalHealthPanel({ refreshTick }) {
    const { health, refresh } = useOperationalHealth();
    const [starting, setStarting] = useState(false);
    const [startResult, setStartResult] = useState(null);
    const [working, setWorking] = useState(null);
    const history = useHistory();

    if (!health || health.status === 'green') return null;

    async function runAction(issueId, fn) {
        setWorking(issueId);
        setStartResult(null);
        try {
            const r = await fn();
            const msg = r.activated != null ? `${r.activated} automação(ões) ativada(s).`
                : r.started != null ? `${r.started} de ${r.total} monitor(es) iniciado(s).`
                : r.created != null ? `${r.created} monitor(es) criado(s).`
                : r.message || 'Concluído.';
            setStartResult({ ok: true, message: msg });
            setTimeout(refresh, 1200);
        } catch (err) {
            setStartResult({ ok: false, message: err.response?.data?.error || err.message || 'Erro.' });
        } finally {
            setWorking(null);
        }
    }

    // Bulk provisiona IA pra TODOS os símbolos sem cobertura de uma vez (item 5 da fila —
    // "1 clique em vez de N botões por símbolo"). Fora do runAction genérico porque o retorno
    // {created,alreadyCovered,errors} não bate com as heurísticas de mensagem dele.
    async function handleProvisionAllAi(issue) {
        const symbols = issue.symbols || [];
        if (!symbols.length) return;
        const ok = await confirmDialog({
            title: 'Provisionar IA em lote',
            message: `Cria automação de Análise IA (roda a cada 20 min) para ${symbols.length} símbolo(s): ${symbols.map(s => s.symbol).join(', ')}.\n\n⚠️ Isto CONSOME TOKENS de IA por ciclo, por símbolo — usa a cota/custo do seu provedor de IA enquanto estiverem ativas.\n\nSem isto, "Abrir Short (Regra)" já cobre por regime (sem IA) onde existir. Confirma?`,
            confirmLabel: `Provisionar ${symbols.length} (consome tokens)`,
            confirmVariant: 'warning'
        });
        if (!ok) return;
        setWorking(issue.id);
        setStartResult(null);
        try {
            const r = await provisionAllAi(symbols);
            const parts = [];
            if (r.created) parts.push(`${r.created} provisionada(s)`);
            if (r.alreadyCovered) parts.push(`${r.alreadyCovered} já cobertas`);
            if (r.errors.length) parts.push(`${r.errors.length} falharam (${r.errors.map(e => e.symbol).join(', ')})`);
            setStartResult({ ok: r.errors.length === 0, message: parts.join(' · ') || 'Concluído.' });
            setTimeout(refresh, 1200);
        } catch (err) {
            setStartResult({ ok: false, message: err.response?.data?.error || err.message || 'Erro.' });
        } finally {
            setWorking(null);
        }
    }

    async function handleStartAll(mode) {
        setStarting(true);
        setStartResult(null);
        try {
            const r = await startAllMonitors(mode);
            const label = mode === 'basic' ? 'básicos' : mode === 'strategy' ? 'por estratégia' : 'todos';
            setStartResult({ ok: true, message: `${r.started} de ${r.total} monitores (${label}) iniciados.` });
            setTimeout(refresh, 1200);
        } catch (err) {
            setStartResult({ ok: false, message: err.response?.data?.error || err.message || 'Falha ao iniciar.' });
        } finally {
            setStarting(false);
        }
    }

    function actionFor(issue) {
        switch (issue.id) {
            case 'scanner_stopped':
            case 'regime_stopped':
            case 'candles_stopped':
                return { label: '▶ Iniciar básicos', inline: true, fn: () => runAction(issue.id, () => startAllMonitors('basic')) };
            case 'no_scanner':
            case 'no_regime':
            case 'no_candles':
                return { label: '➕ Criar monitores', inline: true, fn: () => runAction(issue.id, () => createMissingMonitors()) };
            case 'ai_scan_inactive':
                return { label: '✓ Ativar scanner', inline: true, fn: () => runAction(issue.id, () => activateByActionType('AI_MARKET_SCAN')) };
            case 'supporting_inactive':
                return { label: '▶ Ativar suporte', inline: true, fn: () => runAction(issue.id, () => activateSupportingAutomations()) };
            case 'no_system_ticker':
                return { label: '▶ Ativar ticker', inline: true, fn: () => runAction(issue.id, () => ensureSystemTicker()) };
            case 'no_ai_scan':
                return { label: issue.actionLabel || 'Templates', inline: false, fn: () => history.push(issue.action) };
            case 'ai_uncovered':
                return { label: `🤖 Provisionar IA (${(issue.symbols || []).length})`, inline: true, fn: () => handleProvisionAllAi(issue) };
            default:
                return { label: issue.actionLabel || 'Ver', inline: false, fn: () => history.push(issue.action) };
        }
    }

    const stoppedMonitors = health.stoppedMonitors || 0;

    return (
        <div className='card border-0 shadow mb-4'>
            <div className='card-body py-3'>
                <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2'>
                    <h6 className='mb-0 fw-bold'>
                        {health.status === 'red' ? '🔴' : '🟡'} Atenção operacional
                    </h6>
                    {stoppedMonitors > 0 && (
                        <div className='btn-group btn-group-sm'>
                            <button className='btn btn-success' disabled={starting} onClick={() => handleStartAll('all')}>
                                {starting ? 'Iniciando...' : `▶ Iniciar monitores`}
                            </button>
                            <button type='button' className='btn btn-success dropdown-toggle dropdown-toggle-split'
                                data-bs-toggle='dropdown' disabled={starting} />
                            <ul className='dropdown-menu dropdown-menu-end'>
                                <li>
                                    <button className='dropdown-item' onClick={() => handleStartAll('basic')}>
                                        <strong>Básicos</strong>
                                        <small className='d-block text-muted'>Candles, Scanner e Regime</small>
                                    </button>
                                </li>
                                <li>
                                    <button className='dropdown-item' onClick={() => handleStartAll('strategy')}>
                                        <strong>Por estratégia ativa</strong>
                                        <small className='d-block text-muted'>Monitores vinculados a estratégias ativas</small>
                                    </button>
                                </li>
                                <li><hr className='dropdown-divider' /></li>
                                <li>
                                    <button className='dropdown-item' onClick={() => handleStartAll('all')}>
                                        <strong>Todos os parados</strong>
                                        <small className='d-block text-muted'>{stoppedMonitors} monitores no total</small>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>

                {startResult && (
                    <div className={`alert ${startResult.ok ? 'alert-success' : 'alert-danger'} py-1 mb-2`} style={{ fontSize: 13 }}>
                        {startResult.message}
                        <button className='btn-close float-end' style={{ fontSize: 10 }} onClick={() => setStartResult(null)} />
                    </div>
                )}

                <div className='d-flex flex-column gap-1'>
                    {health.issues.map(issue => {
                        const style = SEVERITY_STYLE[issue.severity] || SEVERITY_STYLE.yellow;
                        const action = actionFor(issue);
                        return (
                            <div key={issue.id} className='d-flex align-items-center justify-content-between gap-2'
                                style={{ fontSize: 13 }}>
                                <span>
                                    <span className={`badge ${style.badge} me-2`} style={{ fontSize: 10 }}>
                                        {issue.severity === 'red' ? 'Crítico' : 'Atenção'}
                                    </span>
                                    {issue.text}
                                </span>
                                <button
                                    className={`btn btn-sm py-0 px-2 flex-shrink-0 ${action.inline ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                                    style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                                    disabled={working === issue.id}
                                    onClick={action.fn}>
                                    {working === issue.id ? '...' : action.label}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default OperationalHealthPanel;
